import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/es";
import {
  getFirestore,
  collection,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
  query,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import Nav from "./Nav";
import Spinner from "./Spinner";
import Swal from "sweetalert2";
import ModalCreateEvent from "./ModalCreateEvent";
import PedidosActivos from "./PedidosActivos";
import ArchivosRecientes from "./ArchivosRecientes";
import { useAuth } from "../context/AuthContext";

moment.locale("es");

const Dashboard = ({
  closeSidebar,
  ordenesDeCompra,
  calculateDaysLeft,
  openModal,
  modalData,
  closeModal,
  agregarComentario,
  comentarios,
  setNuevoComentario,
  nuevoComentario,
}) => {
  const { orgId } = useAuth();
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(moment());
  const [proximasAVencer, setProximasAVencer] = useState([]);
  const [proximosEventos, setProximosEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();
  const [tab, setTab] = useState("Todos");
  const [selectedDate, setSelectedDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeMenu, setActiveMenu] = useState(null);
  const [modalCreateIsOpen, setModalCreateIsOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  const fetchCategories = useCallback(() => {
    if (!orgId) return () => {};

    const firestore = getFirestore();
    const catRef = collection(firestore, `orgs/${orgId}/filtrosCategorias`);
    const q = query(catRef);

    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setCategories(data);
      },
      (err) => console.error("filtrosCategorias error:", err),
    );

    return () => unsub();
  }, [orgId]);

  const fetchTareas = useCallback(() => {
    if (!orgId) return () => {};

    const firestore = getFirestore();
    const tareasRef = collection(firestore, `orgs/${orgId}/tareaCalendario`);
    const q = query(tareasRef);

    const unsub = onSnapshot(
      q,
      (snap) => {
        const tareas = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const tareasEvents = tareas.map((t) => ({
          id: `tarea-${t.id}`,
          title: t.title || "Tarea sin título",
          start: moment(t.from).toDate(),
          end: moment(t.to).toDate(),
          resource: "tarea",
          color: t.color || "#f0f0f0",
          category: t.category || "Tarea",
          allDay: false,
          style: { backgroundColor: t.color || "#f0f0f0" },
        }));

        setEvents((prev) => [
          ...prev.filter((e) => e.resource !== "tarea"),
          ...tareasEvents,
        ]);
      },
      (err) => console.error("tareaCalendario error:", err),
    );

    return () => unsub();
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;

    /* 1) Listener de categorías (orgs/{orgId}/filtrosCategorias) */
    const unsubCat = fetchCategories();

    /* 2) Listener de cotizaciones (orgs/{orgId}/cotizaciones) */
    const fetchCotizaciones = () => {
      const firestore = getFirestore();
      const cotRef = collection(firestore, `orgs/${orgId}/cotizaciones`);
      const q = query(cotRef);

      return onSnapshot(
        q,
        (snap) => {
          const cotizaciones = snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));

          const proximas = cotizaciones
            .filter((c) =>
              moment(c.fechaVencimiento).isSameOrAfter(moment().startOf("day")),
            )
            .sort(
              (a, b) => moment(a.fechaVencimiento) - moment(b.fechaVencimiento),
            );

          setProximasAVencer(proximas.slice(0, 6));

          const cotEvents = proximas.map((c) => ({
            id: `cotizacion-${c.id}`,
            title: `Cotización #${c.numeroCotizacion}`,
            start: moment.utc(c.fechaVencimiento).toDate(),
            end: moment.utc(c.fechaVencimiento).toDate(),
            resource: "cotizacion",
            allDay: false,
            color: "#dbeafe",
          }));

          setEvents((prev) => [
            ...prev.filter((ev) => ev.resource !== "cotizacion"),
            ...cotEvents,
          ]);
        },
        (err) => console.error("cotizaciones error:", err),
      );
    };

    /* 3) Listener de eventos (orgs/{orgId}/eventos) */
    const fetchEventos = () => {
      const firestore = getFirestore();
      const eveRef = collection(firestore, `orgs/${orgId}/eventos`);
      const q = query(eveRef);

      return onSnapshot(
        q,
        (snap) => {
          const eventos = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

          const proximos = eventos
            .filter((ev) =>
              moment(ev.to).isSameOrAfter(moment().startOf("day")),
            )
            .sort((a, b) => moment(a.to) - moment(b.to));

          setProximosEventos(proximos.slice(0, 6));

          const eventosCal = proximos.map((ev) => ({
            id: `evento-${ev.id}`,
            title: ev.title,
            start: moment(ev.from).toDate(),
            end: moment(ev.to).toDate(),
            resource: "evento",
            color: ev.color || "#229954",
            style: {
              backgroundColor: ev.color || "#229954",
              padding: "auto",
              fontSize: "12px",
              width: "80%",
              margin: "0 auto",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              height: "auto",
            },
          }));

          setEvents((prev) => [
            ...prev.filter((ev) => ev.resource !== "evento"),
            ...eventosCal,
          ]);

          setLoading(false);
        },
        (err) => console.error("eventos error:", err),
      );
    };

    /* 4) Fechas festivas (local) */
    const getFechasFestivas = () => {
      const base = [
        { title: "Año Nuevo", month: "01", day: "01", color: "#de2e03" },
        {
          title: "Día de la Constitución",
          month: "02",
          day: "05",
          color: "#de2e03",
        },
        {
          title: "Natalicio de Benito Juárez",
          month: "03",
          day: "21",
          color: "#de2e03",
        },
        { title: "Día del Trabajo", month: "05", day: "01", color: "#de2e03" },
        {
          title: "Independencia de México",
          month: "09",
          day: "16",
          color: "#de2e03",
        },
        {
          title: "Transición del Poder Ejecutivo",
          month: "10",
          day: "01",
          color: "#de2e03",
        },
        {
          title: "Revolución Mexicana",
          month: "11",
          day: "20",
          color: "#de2e03",
        },
        { title: "Navidad", month: "12", day: "25", color: "#de2e03" },
      ];

      const year = moment().year();
      return base.map((f) => {
        const fecha = moment(
          `${year}-${f.month}-${f.day}`,
          "YYYY-MM-DD",
        ).toDate();
        return {
          id: `festivo-${f.title}-${year}`,
          title: f.title,
          start: fecha,
          end: fecha,
          allDay: true,
          resource: "festivo",
          color: f.color,
          style: {
            backgroundColor: f.color,
            padding: "auto",
            fontSize: "12px",
            width: "80%",
            margin: "0 auto",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            height: "auto",
          },
        };
      });
    };

    setEvents((prev) => [
      ...prev.filter((ev) => !ev.id.startsWith("festivo-")),
      ...getFechasFestivas(),
    ]);

    /* 5) Iniciar listeners y cleanup */
    const unsubCot = fetchCotizaciones();
    const unsubEve = fetchEventos();
    const unsubTar = fetchTareas();

    return () => {
      unsubCat && unsubCat();
      unsubCot && unsubCot();
      unsubEve && unsubEve();
      unsubTar && unsubTar();
    };
  }, [orgId, fetchCategories, fetchTareas]);

  // ─────────────────────────────────────────────────────────────────
  // 4) Lógica del Modal de creación/edición
  // ─────────────────────────────────────────────────────────────────
  const openCreateModal = (event = null) => {
    if (event) {
      // Es edición
      setEditingEvent({
        ...event,
        // Para el input datetime-local
        date: moment(event.start).format("YYYY-MM-DDTHH:mm"),
      });
    } else {
      setEditingEvent(null);
    }
    setModalCreateIsOpen(true);
  };

  const closeCreateModal = () => {
    setModalCreateIsOpen(false);
    setEditingEvent(null);
  };

  const handleSaveEvent = async (data) => {
    try {
      if (!orgId) return;

      const firestore = getFirestore();
      const from = moment(data.date).toISOString();
      const to = from;

      const isEvento = data.category === "Eventos";
      const collectionName = isEvento ? "eventos" : "tareaCalendario";
      const newResource = isEvento ? "evento" : "tarea";

      const basePayload = {
        title: data.title,
        from,
        to,
        color: data.color || "#f0f0f0",
        category: isEvento ? null : data.category,
        resource: newResource,
        // ✅ ya NO ownerId, ya NO invited
      };

      const basePath = `orgs/${orgId}/${collectionName}`;

      if (editingEvent) {
        // editingEvent.id viene como: "evento-xxx" o "tarea-xxx" (o id limpio)
        const oldResource = editingEvent.resource; // "evento" | "tarea"
        const oldCol = oldResource === "evento" ? "eventos" : "tareaCalendario";
        const newCol = collectionName;

        const idClean = `${editingEvent.id}`.replace(
          /^(evento-|tarea-|cotizacion-)/,
          "",
        );

        if (oldCol === newCol) {
          await updateDoc(
            doc(firestore, `orgs/${orgId}/${newCol}/${idClean}`),
            basePayload,
          );
        } else {
          await deleteDoc(doc(firestore, `orgs/${orgId}/${oldCol}/${idClean}`));
          await addDoc(collection(firestore, basePath), basePayload);
        }
      } else {
        await addDoc(collection(firestore, basePath), basePayload);
      }

      Swal.fire({
        title: "Guardado",
        text: "Acción exitosa",
        icon: "success",
        scrollbarPadding: false,
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        title: "Error",
        text: "No se pudo guardar",
        icon: "error",
        scrollbarPadding: false,
      });
    }
    closeCreateModal();
  };

  // Eliminar de la lista local (cuando no es un "evento" de la DB sino cotización/festivo)
  const handleRemoveFromList = (eventId) => {
    setEvents((prev) => prev.filter((ev) => ev.id !== eventId));
    Swal.fire({
      title: "Eliminado",
      text: "Evento eliminado de la lista",
      icon: "info",
      scrollbarPadding: false,
    });
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      if (!orgId) return;

      const { isConfirmed } = await Swal.fire({
        title: "¿Estás seguro?",
        text: "Esta acción eliminará el evento de forma permanente.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
        scrollbarPadding: false,
      });
      if (!isConfirmed) return;

      const firestore = getFirestore();

      // eventId aquí llega como: "evento-abc" o "tarea-abc"
      const isTarea = `${eventId}`.startsWith("tarea-");
      const collectionName = isTarea ? "tareaCalendario" : "eventos";
      const docId = `${eventId}`.replace(/^(evento-|tarea-)/, "");

      const ref = doc(firestore, `orgs/${orgId}/${collectionName}/${docId}`);

      const snap = await getDoc(ref);
      if (!snap.exists()) {
        Swal.fire({
          title: "Error",
          text: "El evento ya no existe.",
          icon: "error",
          scrollbarPadding: false,
        });
        return;
      }

      // ✅ ya NO validación por ownerId/invited aquí
      await deleteDoc(ref);
      setEvents((prev) => prev.filter((ev) => ev.id !== eventId));

      Swal.fire({
        title: "Evento eliminado",
        text: "El evento se ha borrado correctamente.",
        icon: "success",
        scrollbarPadding: false,
      });
    } catch (error) {
      console.error("Error al eliminar evento:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudo eliminar el evento.",
        icon: "error",
        scrollbarPadding: false,
      });
    }
  };

  const handleAddCategory = async (newCat, color) => {
    try {
      if (!orgId) return;

      const firestore = getFirestore();
      await addDoc(collection(firestore, `orgs/${orgId}/filtrosCategorias`), {
        label: newCat,
        color: color || "#f0f0f0",
        // ✅ ya NO ownerId, ya NO invited
      });

      Swal.fire({
        title: "Categoría Creada",
        text: "Se agregó correctamente",
        icon: "success",
        scrollbarPadding: false,
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        title: "Error",
        text: "No se pudo crear la categoría",
        icon: "error",
        scrollbarPadding: false,
      });
    }
  };

  const upcomingEvents = events.filter((ev) =>
    moment(ev.start).isSameOrAfter(moment().startOf("day"), "day"),
  );

  const handleDayClick = (day) => setSelectedDate(day);

  let tabFilteredEvents = [];
  if (tab === "Cotizaciones") {
    tabFilteredEvents = upcomingEvents
      .filter((ev) => ev.resource === "cotizacion")
      .sort((a, b) => moment(a.start) - moment(b.start))
      .slice(0, 8);
  } else if (tab === "Eventos") {
    tabFilteredEvents = upcomingEvents.filter(
      (ev) => ev.resource === "evento" || ev.resource === "tarea",
    );
  } else if (tab === "Festivos") {
    const startOfCurrentMonth = moment().startOf("month");
    const endOfNextMonth = moment().add(1, "month").endOf("month");
    tabFilteredEvents = upcomingEvents
      .filter(
        (ev) =>
          ev.resource === "festivo" &&
          moment(ev.start).isBetween(
            startOfCurrentMonth,
            endOfNextMonth,
            "day",
            "[]",
          ),
      )
      .sort((a, b) => moment(a.start) - moment(b.start))
      .slice(0, 8);
  } else if (tab === "Tareas") {
    tabFilteredEvents = upcomingEvents
      .filter((ev) => ev.resource === "tarea")
      .sort((a, b) => moment(a.start) - moment(b.start))
      .slice(0, 8);
  } else if (tab === "Todos") {
    tabFilteredEvents = upcomingEvents
      .sort((a, b) => moment(a.start) - moment(b.start))
      .slice(0, 8);
  }

  let displayedEvents = selectedDate
    ? events.filter((ev) => moment(ev.start).isSame(selectedDate, "day"))
    : tabFilteredEvents;

  let filteredEvents = displayedEvents;
  if (searchTerm.trim() !== "") {
    filteredEvents = displayedEvents.filter((event) =>
      event.title.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }

  const finalDisplayedEvents = filteredEvents;

  const handleCotizacionClick = () => {
    navigate("/cotizacion", { state: { openModal: true } });
    if (closeSidebar) closeSidebar();
  };
  const handleOrdenClick = () => {
    navigate("/orden", { state: { openModal: true } });
    if (closeSidebar) closeSidebar();
  };
  const handleRemisionClick = () => {
    navigate("/remisiones", { state: { openModal: true } });
    if (closeSidebar) closeSidebar();
  };
  const navigateToCalendario = () => {
    navigate("/calendario");
  };
  const navigateToTareas = () => {
    navigate("/tareas");
  };

  const handlePrev = () =>
    setCurrentDate(currentDate.clone().subtract(1, "week"));
  const handleNext = () => setCurrentDate(currentDate.clone().add(1, "week"));

  const daysOfWeek = Array.from({ length: 7 }).map((_, i) =>
    currentDate.clone().startOf("week").add(i, "days"),
  );

  const handleOpenCreateModal = () => {
    // Limpio editingEvent y abro:
    openCreateModal();
  };

  const handleEditEvent = (event) => {
    openCreateModal(event);
    setActiveMenu(null);
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <>
      <Nav
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
      />

      <div style={{ paddingLeft: "70px" }}>
        <div className="categories-container">
          <h3
            style={{
              fontWeight: "bold",
              textAlign: "left",
              gridColumn: "1 / -1",
              margin: 0,
            }}
          >
            Accesos Rápidos
          </h3>
          <div className="category cotizacion" onClick={handleCotizacionClick}>
            <i className="bi bi-file-text" style={{ color: "#329B24" }}></i>
            <span>Cotización</span>
          </div>
          <div className="category orden" onClick={handleOrdenClick}>
            <i
              className="bi bi-file-earmark-text"
              style={{ color: "#0000FF" }}
            ></i>
            <span>Orden de Compra</span>
          </div>
          <div className="category evento" onClick={handleOpenCreateModal}>
            <i
              className="bi bi-calendar-event"
              style={{ color: "#F44336" }}
            ></i>
            <span>Evento</span>
          </div>
          <div className="category remision" onClick={handleRemisionClick}>
            <i className="bi bi-receipt" style={{ color: "#b100cd" }}></i>
            <span>Remisión</span>
          </div>
          <div className="category reporte-servicio">
            <i
              className="bi bi-file-earmark-bar-graph"
              style={{ color: "#4fc3f7" }}
            ></i>
            <span>Reporte de Servicio</span>
          </div>
          <div className="category tarea" onClick={navigateToTareas}>
            <i className="bi bi-list-task" style={{ color: "#fb8c00" }}></i>
            <span>Tarea</span>
          </div>
        </div>
      </div>

      <PedidosActivos
        ordenesDeCompra={ordenesDeCompra}
        calculateDaysLeft={calculateDaysLeft}
        openModal={openModal}
        modalData={modalData}
        closeModal={closeModal}
        agregarComentario={agregarComentario}
        comentarios={comentarios}
        setNuevoComentario={setNuevoComentario}
        nuevoComentario={nuevoComentario}
      />

      <ArchivosRecientes />

      {/* CALENDARIO SIMPLE */}
      <div className="dashboard-container">
        <div className="simple-calendar">
          <div className="calendar-header">
            <h3>Calendario</h3>
            <button className="see-all-button" onClick={navigateToCalendario}>
              Ver Todo
            </button>
          </div>

          <div className="calendar-navigation">
            <button className="nav-button" onClick={handlePrev}>
              <i className="bi bi-chevron-left"></i>
            </button>
            <span className="current-month">
              {currentDate.format("MMM YYYY")}
            </span>
            <button className="nav-button" onClick={handleNext}>
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>

          <div className="calendar-days">
            {daysOfWeek.map((day, index) => {
              const isToday = currentDate.isSame(day, "day");
              const isSelected =
                selectedDate && selectedDate.isSame(day, "day");

              let dayClass = "";
              if (isToday) {
                dayClass = "selected";
              }
              if (isSelected) {
                dayClass = "clicked";
              }

              return (
                <div
                  key={index}
                  className={`day ${dayClass}`}
                  onClick={() => handleDayClick(day)}
                >
                  <span className={`day-name ${dayClass}`}>
                    {day.format("ddd")}
                  </span>
                  <span className="day-number">{day.format("DD")}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="calendar-search-bar">
          <input
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <i className="bi bi-search"></i>
        </div>

        <div className="tabs">
          <button
            className={`tab ${tab === "Todos" ? "active" : ""}`}
            onClick={() => {
              setTab("Todos");
              setSelectedDate(null);
            }}
          >
            Todos
          </button>
          <button
            className={`tab ${tab === "Cotizaciones" ? "active" : ""}`}
            onClick={() => {
              setTab("Cotizaciones");
              setSelectedDate(null);
            }}
          >
            Cotizaciones
          </button>
          <button
            className={`tab ${tab === "Eventos" ? "active" : ""}`}
            onClick={() => {
              setTab("Eventos");
              setSelectedDate(null);
            }}
          >
            Eventos
          </button>
          <button
            className={`tab ${tab === "Festivos" ? "active" : ""}`}
            onClick={() => {
              setTab("Festivos");
              setSelectedDate(null);
            }}
          >
            Festivos
          </button>
        </div>

        <div className="events-list">
          {finalDisplayedEvents.length === 0 && searchTerm.trim() === "" ? (
            <p style={{ marginTop: "50px", textAlign: "center" }}>
              No hay cotizaciones, eventos, tareas o festivos.
            </p>
          ) : (
            finalDisplayedEvents.map((event) => {
              // Determina la clase CSS según el resource
              let customClass = "";
              if (event.resource === "festivo") {
                customClass = "festivo-bg";
              } else if (
                // <-- AQUI unificas "evento" y "tarea"
                event.resource === "evento" ||
                event.resource === "tarea"
              ) {
                customClass = "evento-bg"; // <-- Mismo borde verde
              } else if (event.resource === "cotizacion") {
                customClass = "cotizacion-bg";
              }

              return (
                <div
                  key={`${event.resource}-${event.id}`}
                  className={`event-item ${customClass}`}
                >
                  <div className="event-side-bar"></div>
                  <div className="event-content">
                    <div className="event-header">
                      <h4 className="event-title">{event.title}</h4>
                      <div style={{ position: "relative" }}>
                        <button
                          className="dropdown-button"
                          onClick={() =>
                            setActiveMenu(
                              activeMenu === event.id ? null : event.id,
                            )
                          }
                        >
                          <i className="bi bi-three-dots"></i>
                        </button>
                        {activeMenu === event.id && (
                          <div className="event-options-menu">
                            {/* Si es un 'evento' o 'tarea' => podemos editar/borrar de la DB */}
                            {event.resource === "evento" ||
                            event.resource === "tarea" ? (
                              <>
                                <button
                                  onClick={() => handleEditEvent(event)}
                                  className="editarEvento"
                                >
                                  <i
                                    className="bi bi-pencil"
                                    style={{ marginRight: "5px" }}
                                  ></i>
                                  Editar
                                </button>
                                <button
                                  onClick={() =>
                                    handleDeleteEvent(
                                      `${event.resource}-${event.id}`,
                                    )
                                  }
                                  className="eliminarEvento"
                                >
                                  <i
                                    className="bi bi-trash"
                                    style={{ marginRight: "5px" }}
                                  ></i>
                                  Eliminar
                                </button>
                              </>
                            ) : (
                              // Si es cotizacion/festivo => No tienes doc en la misma colec, quítalo local
                              <button
                                onClick={() =>
                                  handleRemoveFromList(
                                    `${event.resource}-${event.id}`,
                                  )
                                }
                                className="eliminarEvento"
                              >
                                <i
                                  className="bi bi-trash"
                                  style={{ marginRight: "5px" }}
                                ></i>
                                Eliminar
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="event-time">
                      {event.resource === "cotizacion"
                        ? "Vence el "
                        : event.resource === "tarea"
                          ? "Fecha: "
                          : "Fecha: "}
                      {moment
                        .utc(event.end)
                        .local()
                        .format("dddd, DD [de] MMMM - hh:mm A")}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* NUESTRO MODAL DE CREACIÓN/EDICIÓN DE EVENTO O TAREA */}
      <ModalCreateEvent
        isOpen={modalCreateIsOpen}
        onClose={closeCreateModal}
        onSave={handleSaveEvent}
        initialData={editingEvent}
        categories={categories} // Lista de categorías
        onAddCategory={handleAddCategory} // Función para crear nueva cat
      />
    </>
  );
};

export default Dashboard;
